import React from 'react';
import { Alert, StyleSheet, Text } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { CalcScreen, CategoryCard, ListRow } from '../../components/shell';
import { C, FONT, SP } from '../../theme/tokens';

type Calc = { label: string; description: string };
type AirCategory = {
  title: string;
  description: string;
  emoji: string;
  calculations: Calc[];
};

const AIR_CATEGORIES: AirCategory[] = [
  {
    title: 'Airflow & Velocity',
    description: 'Basic airflow calculations and measurements',
    emoji: '💨',
    calculations: [
      { label: 'Volumetric Flow Rate', description: 'Solve Q = V × A for CFM, velocity, or duct area' },
      { label: 'Total Pressure', description: 'Combine static and velocity pressure into total pressure' },
      { label: 'Velocity of Air', description: 'Convert velocity pressure to FPM, density-corrected' },
      { label: 'Pitot Traverse Averaging', description: 'Average multi-point VP readings into a single velocity' },
      { label: 'Pitot Traverse Grid', description: 'Log/Tchebycheff grid traverse with CFM and uniformity' },
      { label: 'Duct Traverse Points', description: 'Count and place traverse points for round or rectangular duct' },
      { label: 'Flow Hood K-Factor', description: 'Solve K, VP, or CFM for flow-hood or pitot-style devices' },
      { label: 'Duct Leakage (Class)', description: 'Allowable leakage from pressure class and surface area' },
      { label: 'Air Changes', description: 'ACH from CFM and volume, or size flow to a target ACH' },
      { label: 'Duct Area', description: 'Cross-sectional area for round, square, or rectangular duct' },
      { label: 'Duct Size from CFM', description: 'Pick duct size from target CFM and design velocity' },
      { label: 'Equivalent Round Duct', description: 'Round-duct equivalent of a rectangular cross-section' },
      { label: 'Hood Face Velocity', description: 'Face velocity and CFM across an open hood face' },
    ],
  },
  {
    title: 'Ductwork Losses',
    description: 'Friction loss, fitting loss, and equivalent length',
    emoji: '🧮',
    calculations: [
      { label: 'Duct Friction Loss', description: 'Straight-duct friction loss per 100 ft and total drop' },
      { label: 'Fitting Loss & Equivalent Length', description: 'Fitting loss from loss coefficient or equivalent length' },
      { label: 'Filter Loading ΔP', description: 'Estimate filter pressure drop as loading increases' },
    ],
  },
  {
    title: 'Air Temperature',
    description: 'Temperature and psychrometric calculations',
    emoji: '🌡️',
    calculations: [
      { label: 'Mixed Air Temperature', description: 'Blended supply temp from outside and return air mix' },
      { label: 'Mixed Air Enthalpy', description: 'Blended enthalpy across outside and return air streams' },
      { label: 'Outside Air Percentage', description: 'Back-calculate %OA from mixed, outside, and return temps' },
      { label: 'Psychrometrics', description: 'Full air state: DB, WB, dew point, RH, W, h, specific volume' },
      { label: 'Mixed Air (Psychrometrics)', description: 'Mixed-air state point from two source air conditions' },
    ],
  },
  {
    title: 'Heat Transfer',
    description: 'Heat transfer and energy calculations',
    emoji: '🔥',
    calculations: [
      { label: 'Heat Transfer (Total)', description: 'Total heat as the sum of sensible and latent loads' },
      { label: 'Sensible Heat Transfer', description: 'Sensible BTU/h from CFM and temperature difference' },
      { label: 'Latent Heat Transfer', description: 'Latent BTU/h from CFM and humidity ratio change' },
      { label: 'Sensible Heat Ratio', description: 'SHR as the sensible fraction of total load' },
      { label: 'Q Btuh', description: 'General Q = 1.08 × CFM × ΔT for quick sizing' },
    ],
  },
  {
    title: 'Fan Equations',
    description: 'Fan performance and affinity laws',
    emoji: '🔄',
    calculations: [
      { label: 'Fan Affinity Laws', description: 'Scale CFM, SP, and BHP when RPM or diameter changes' },
      { label: 'Tip Speed', description: 'Blade tip speed from fan diameter and RPM' },
    ],
  },
  {
    title: 'Sheave Equations',
    description: 'Pulley and belt calculations',
    emoji: '⚙️',
    calculations: [
      { label: 'RPM and PD', description: 'Solve fan/motor RPM and pitch diameter from the drive ratio' },
      { label: 'Fan Belt Length', description: 'V-belt length from sheave diameters and center distance' },
    ],
  },
  {
    title: 'Tools',
    description: 'Quick field conversions and utilities',
    emoji: '🧰',
    calculations: [
      { label: 'Unit Converter', description: 'Convert between common HVAC units on the fly' },
    ],
  },
];

const SCREEN_MAP: Record<string, string> = {
  'Volumetric Flow Rate': 'AirflowVolumetricFlowRate',
  'Total Pressure': 'AirflowTotalPressure',
  'Velocity of Air': 'AirflowVelocityOfAir',
  'Pitot Traverse Averaging': 'AirflowPitotTraverseAveraging',
  'Pitot Traverse Grid': 'AirflowPitotTraverseGrid',
  'Duct Traverse Points': 'DuctTraverse',
  'Duct Size from CFM': 'AirflowDuctSizeFromCFM',
  'Equivalent Round Duct': 'AirflowEquivalentRoundDuct',
  'Flow Hood K-Factor': 'AirflowFlowHoodKFactor',
  'Duct Leakage (Class)': 'AirflowDuctLeakageClass',
  'Air Changes': 'AirflowAirChanges',
  'Duct Area': 'AirflowDuctArea',
  'Mixed Air Temperature': 'AirflowMixedAirTemperature',
  'Mixed Air Enthalpy': 'AirTemperatureMixedAirEnthalpy',
  'Outside Air Percentage': 'AirTemperatureOutsideAirPercentage',
  'Psychrometrics [Legacy]': 'PsychrometricsDewPointEnthalpy',
  'Mixed Air (Psychrometrics) [Legacy]': 'PsychrometricsMixedAir',
  'Heat Transfer (Total)': 'HeatTransferHeatTransferTotal',
  'Sensible Heat Transfer': 'HeatTransferSensibleHeatTransfer',
  'Latent Heat Transfer': 'HeatTransferLatentHeatTransfer',
  'Sensible Heat Ratio': 'HeatTransferSensibleHeatRatio',
  'Q Btuh': 'HeatTransferQBtuh',
  'Fan Affinity Laws': 'FanEquationsFanAffinityLaws',
  'Tip Speed': 'FanEquationsTipSpeed',
  'RPM and PD': 'SheaveEquationsRPMandPD',
  'Fan Belt Length': 'SheaveEquationsFanBeltLength',
  'Hood Face Velocity': 'AirflowHoodFaceVelocity',
  'Fitting Loss & Equivalent Length': 'DuctFittingLossEquivalentLength',
  'Duct Friction Loss': 'DuctFrictionLoss',
  'Filter Loading ΔP': 'FilterPressureDrop',
  'Psychrometrics': 'PsychrometricsDewPointEnthalpy',
  'Mixed Air (Psychrometrics)': 'PsychrometricsMixedAir',
  'Unit Converter': 'UnitConversions',
};

const TABAirScreen = () => {
  const navigation = useNavigation();

  const handleCalculationPress = (calculation: string) => {
    const screenName = SCREEN_MAP[calculation];
    if (screenName) {
      navigation.navigate(screenName as never);
    } else {
      Alert.alert('Coming Soon', `${calculation} calculator will be available soon!`);
    }
  };

  return (
    <CalcScreen title="Air Calculations" subtitle="TAB · Air">
      <Text style={styles.sectionLabel}>Air Categories</Text>
      {AIR_CATEGORIES.map((category) => (
        <CategoryCard
          key={category.title}
          emoji={category.emoji}
          title={category.title}
          description={category.description}
        >
          {category.calculations.map((calculation, i) => (
            <ListRow
              key={calculation.label}
              index={i}
              label={calculation.label}
              description={calculation.description}
              onPress={() => handleCalculationPress(calculation.label)}
            />
          ))}
        </CategoryCard>
      ))}
    </CalcScreen>
  );
};

const styles = StyleSheet.create({
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: SP.sm,
  },
});

export default TABAirScreen;
